import type { ShapeManifest } from '../../../lib/diagram/shape-manifest.types';
import { z } from 'zod';
import React from 'react';
import { CompassContainerCard } from '../../compass/canvas/renderers/CompassContainerCard';

/**
 * `compass_bubble` — used by Compass Explorer aggregate zoom levels:
 * L1/L2/L3 containers, L4 derived workloads, and unfiltered L5
 * resource-type overviews. Bubbles are selectable like normal canvas
 * nodes; the in-card drill affordance handles depth navigation.
 */

const compassBubbleProps = z.object({
  label: z.string().default(''),
  kind: z.enum(['account', 'region', 'vpc', 'subnet', 'workload', 'resource_type']).default('vpc'),
  referenceId: z.string().default(''),
  resourceCount: z.number().default(0),
  /** Short resource-type → count. Top 3 rendered as chips. */
  tierBreakdown: z.record(z.string(), z.number()).default({}),
  fillColor: z.string().default('#FFFFFF'),
  strokeColor: z.string().default('#94a3b8'),
  accentColor: z.string().default('#6366f1'),
});

export type CompassBubbleProps = z.infer<typeof compassBubbleProps>;

const KIND_ACCENT: Record<CompassBubbleProps['kind'], string> = {
  account: '#2a4a78',
  region: '#466493',
  vpc: '#1ea672',
  subnet: '#8593a8',
  workload: '#2a73e4',
  resource_type: '#5b6b85',
};

function renderBubbleSvg(
  viewModel: { width?: number; height?: number },
  props: Record<string, unknown>,
): string {
  const w = viewModel.width ?? 260;
  const h = viewModel.height ?? 160;
  const kind = (props.kind as CompassBubbleProps['kind']) ?? 'vpc';
  const label = String(props.label ?? '');
  const count = Number(props.resourceCount ?? 0);
  const accent = KIND_ACCENT[kind] ?? '#6366f1';
  const escaped = label.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const breakdown = (props.tierBreakdown as Record<string, number> | undefined) ?? {};
  const chips = Object.entries(breakdown)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 3)
    .map(([k, v]) => `${k} ×${v}`);

  const parts: string[] = [
    `<rect x="0" y="0" width="${w}" height="${h}" rx="8" ry="8" fill="#ffffff" stroke="#d6dbe3" stroke-width="1.25" />`,
    `<rect x="0" y="0" width="${w}" height="38" rx="8" ry="8" fill="#f7f9fc" />`,
    `<rect x="0" y="0" width="${w}" height="3" rx="8" ry="8" fill="${accent}" />`,
    `<rect x="12" y="12" width="7" height="13" rx="3.5" ry="3.5" fill="${accent}" />`,
    `<text x="26" y="23" font-size="12" font-weight="700" fill="#0a1f3d" font-family="DM Sans, Inter, Arial, sans-serif">${kind.toUpperCase()} · ${count}</text>`,
    `<text x="12" y="62" font-size="14" font-weight="700" fill="#0e1726" font-family="DM Sans, Inter, Arial, sans-serif">${escaped}</text>`,
    `<text x="12" y="82" font-size="12" fill="#5b6b85" font-family="DM Sans, Inter, Arial, sans-serif">${count} resources</text>`,
  ];

  chips.forEach((chip, i) => {
    const y = 96 + i * 20;
    parts.push(
      `<rect x="12" y="${y}" width="${Math.min(w - 24, 16 + chip.length * 6)}" height="16" rx="8" ry="8" fill="#eef2f8" />`,
      `<text x="18" y="${y + 12}" font-size="10" fill="#2d3a52" font-family="DM Sans, Inter, Arial, sans-serif">${chip}</text>`,
    );
  });

  return parts.join('');
}

export const CompassBubbleManifest: ShapeManifest<CompassBubbleProps> = {
  id: 'compass_bubble',
  version: '1.0.0',
  category: 'network',
  icon: 'layers',
  propsSchema: compassBubbleProps,
  defaultProps: () => ({
    label: '',
    kind: 'vpc',
    referenceId: '',
    resourceCount: 0,
    tierBreakdown: {},
    fillColor: '#FFFFFF',
    strokeColor: '#94a3b8',
    accentColor: '#6366f1',
  }),
  defaultDimensions: { width: 260, height: 160 },
  anchors: {
    snap: ['top', 'right', 'bottom', 'left', 'center'],
    perimeter: 'rect',
  },
  capabilities: {
    selectable: true,
    transformable: false,
    textEditable: false,
    connectable: false,
  },
  properties: [
    { key: 'label', label: 'Name', kind: 'string', required: true, ui: { group: 'identity', priority: 1 } },
    { key: 'kind', label: 'Kind', kind: 'string', ui: { group: 'identity', priority: 2 } },
    { key: 'resourceCount', label: 'Resources', kind: 'number', ui: { group: 'identity', priority: 3 } },
  ],
  acl: () => true,
  renderers: {
    svg: (args: { viewModel: unknown; props: unknown }) =>
      renderBubbleSvg(
        args.viewModel as { width?: number; height?: number },
        args.props as Record<string, unknown>,
      ),
    // Preferred path at runtime — NodesRenderer dispatches to
    // ManifestKonvaNode when konvaNative is present, so the Compass
    // Explorer canvas bypasses the generic card renderer and draws the
    // bubble's kind-coloured header + tier chips directly.
    konvaNative: (args) =>
      React.createElement(CompassContainerCard, {
        viewModel: args.viewModel as { width: number; height: number },
        props: args.props as Record<string, unknown>,
      }),
  },
};
